import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  IconButton,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import LeaveAllocationForm from '../../../components/Forms/LeaveAllocationForm';
import { leaveAllocationService, roleService, userService } from '../../../services/apiService';
import { showToast } from '../../../utils/toast';

const LeaveAllocationTab = ({ onAddRef }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [rolesRes, usersRes, allocRes] = await Promise.all([
          roleService.getAll(),
          userService.getAll(),
          leaveAllocationService.getAll(),
        ]);
        const roles = rolesRes?.data || rolesRes || [];
        const users = usersRes?.data || usersRes || [];
        const allocations = allocRes?.data || allocRes || [];
        const teacherRole = Array.isArray(roles) ? roles.find(r => r.role_name?.toLowerCase() === 'teacher') : null;
        const teacherUsers = teacherRole ? (Array.isArray(users) ? users.filter(u => u.role_id === teacherRole.id) : []) : [];
        setTeachers(teacherUsers);
        setAllocations(Array.isArray(allocations) ? allocations : []);
      } catch (err) {
        console.error('Error loading leave allocations:', err);
        setError(err?.response?.data?.error || err?.message || 'Failed to load leave allocations');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleAdd = useCallback(() => { setEditing(null); setModalOpen(true); }, []);

  useEffect(() => {
    if (onAddRef) {
      onAddRef(handleAdd);
    }
  }, [handleAdd, onAddRef]);
  const handleEditRow = (row) => { setEditing(row); setModalOpen(true); };
  const handleModalClose = () => { setModalOpen(false); setEditing(null); };
  const handleModalSave = async (formData) => {
    try {
      setModalLoading(true);
      if (editing) {
        await leaveAllocationService.update(editing.id, formData);
        showToast.success('Leave allocation updated successfully');
      } else {
        await leaveAllocationService.create(formData);
        showToast.success('Leave allocation created successfully');
      }
      const res = await leaveAllocationService.getAll();
      const allocations = res?.data || res || [];
      setAllocations(Array.isArray(allocations) ? allocations : []);
      setEditing(null);
      setModalOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Action failed';
      showToast.error(msg);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (row) => {
    const teacherName = teachers.find(t => t.id === row.teacher_id)?.full_name || 'this allocation';
    if (!window.confirm(`Are you sure you want to delete leave allocation for ${teacherName}?`)) {
      return;
    }
    try {
      setLoading(true);
      await leaveAllocationService.delete(row.id);
      showToast.success('Leave allocation deleted successfully');
      const res = await leaveAllocationService.getAll();
      const allocations = res?.data || res || [];
      setAllocations(Array.isArray(allocations) ? allocations : []);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to delete leave allocation';
      showToast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getAvailableTeachers = () => {
    if (editing) {
      const allocatedTeacherIds = allocations.map(a => a.teacher_id);
      return teachers.filter(t => 
        t.id === editing.teacher_id || !allocatedTeacherIds.includes(t.id)
      );
    } else {
      const allocatedTeacherIds = allocations.map(a => a.teacher_id);
      return teachers.filter(t => !allocatedTeacherIds.includes(t.id));
    }
  };

  const getTeacherName = (teacherId) => {
    return teachers.find(t => t.id === teacherId)?.full_name || '—';
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1.5, md: 2 }, 
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
    }}>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : allocations.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No allocations found
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: { xs: 1.5, sm: 1.75 },
          }}
        >
          {allocations.map((allocation) => {
            const balance = allocation.leave_balance ?? ((allocation.total_leaves || 0) - (allocation.leaves_taken || 0));
            return (
              <Paper
                key={allocation.id}
                variant="outlined"
                sx={{
                  p: { xs: 2, sm: 2.4 },
                  borderRadius: 2,
                  borderColor: 'divider',
                  position: 'relative',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main',
                  },
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                      {getTeacherName(allocation.teacher_id)}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Total Leaves:</strong> {allocation.total_leaves || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Leaves Taken:</strong> {allocation.leaves_taken || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Balance:</strong> {balance}
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditRow(allocation)}
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.light',
                          color: 'primary.dark',
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(allocation)}
                      sx={{
                        color: 'error.main',
                        '&:hover': {
                          bgcolor: 'error.light',
                          color: 'error.dark',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      )}

      <LeaveAllocationForm
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSave}
        initialData={editing}
        loading={modalLoading}
        teachers={getAvailableTeachers()}
        title={editing ? 'Edit Leave Allocation' : 'Add Leave Allocation'}
      />
    </Box>
  );
};

export default LeaveAllocationTab;
